import { spawn, spawnSync } from 'node:child_process';
import { mkdtemp, open, rm } from 'node:fs/promises';
import { tmpdir } from 'node:os';
import { join } from 'node:path';
import { StringDecoder } from 'node:string_decoder';
import { createInterfaceId, Implements, Service, ServiceLifetime } from '@gitlab/needle';
import { Logger, withPrefix } from '@gitlab-org/logging';
import { withSuspendedTty } from '@gitlab-org/tui';
import { RuntimeContext } from '../runtime_context';
import { CappedOutputSink } from './capped_output_sink';

/** Placeholder in a `run` command replaced with a temp file path whose contents become the output. */
export const OUTPUT_FILE_PLACEHOLDER = '{{outputFile}}';

export interface PluginCommandRunOptions {
  cwd: string;
  /** Timeout in seconds. Ignored for interactive commands unless explicitly set. */
  timeoutSeconds?: number;
  /**
   * Suspend the host TUI and let the command inherit the terminal.
   * stdout/stderr are not captured — output must come through `{{outputFile}}`.
   */
  interactive?: boolean;
  /** Extra environment variables merged over the host environment. */
  env?: Record<string, string>;
}

export interface PluginCommandRunResult {
  /**
   * `{{outputFile}}` contents when the command declares the placeholder,
   * otherwise captured stdout (always empty for interactive runs).
   */
  output: string;
  /** Captured stderr. Always empty for interactive runs. */
  stderr: string;
  /** Exit code; null when terminated by a signal (see `signal`). */
  exitCode: number | null;
  /** The signal that terminated the command, if any. */
  signal: NodeJS.Signals | null;
  timedOut: boolean;
  /** Whether `output` (not stderr) was cut at the size cap. */
  truncated: boolean;
}

export interface PluginCommandRunner {
  /**
   * Runs a plugin-declared shell command. `args` are passed as positional
   * shell parameters (`$1`, `$2`, …), never interpolated into the command
   * string, so they cannot inject shell syntax.
   */
  run(
    command: string,
    args: string[],
    options: PluginCommandRunOptions,
  ): Promise<PluginCommandRunResult>;
}

export const PluginCommandRunner = createInterfaceId<PluginCommandRunner>('PluginCommandRunner');

const DEFAULT_TIMEOUT_SECONDS = 60;
/** 256 KiB cap on captured stdout, stderr, and `{{outputFile}}` contents. */
const MAX_OUTPUT_BYTES = 256 * 1024;
const KILL_GRACE_MS = 2000;
/**
 * Stdio drain time after `exit` before settling without `close`. A background
 * child holding the inherited pipes (e.g. `mydaemon & echo started`) prevents
 * `close` from ever firing.
 */
const STREAM_DRAIN_GRACE_MS = 200;

@Implements(PluginCommandRunner)
@Service({
  dependencies: [Logger, RuntimeContext],
  lifetime: ServiceLifetime.Singleton,
})
export class DefaultPluginCommandRunner implements PluginCommandRunner {
  #logger: Logger;

  #runtimeContext: RuntimeContext;

  constructor(logger: Logger, runtimeContext: RuntimeContext) {
    this.#logger = withPrefix(logger, '[PluginCommandRunner]');
    this.#runtimeContext = runtimeContext;
  }

  async run(
    command: string,
    args: string[],
    options: PluginCommandRunOptions,
  ): Promise<PluginCommandRunResult> {
    if (process.platform === 'win32') {
      // FIXME: positional-parameter passing is POSIX-shell specific and won't work on Windows
      throw new Error('Plugin command execution is not supported on Windows yet');
    }

    const usesOutputFile = command.includes(OUTPUT_FILE_PLACEHOLDER);
    let tmpDir: string | undefined;
    let outputFilePath: string | undefined;
    let script = command;

    if (usesOutputFile) {
      tmpDir = await mkdtemp(join(tmpdir(), 'duo-plugin-cmd-'));
      const filePath = join(tmpDir, 'output');
      outputFilePath = filePath;
      // Replacer callback so `$` sequences in the path aren't treated as replacement patterns.
      script = command.replaceAll(OUTPUT_FILE_PLACEHOLDER, () => filePath);
    }

    try {
      const result = options.interactive
        ? this.#runInteractive(script, args, options)
        : await this.#runCaptured(script, args, options);

      if (outputFilePath) {
        // File contents replace stdout, so only the file's truncation matters.
        const { content, truncated } = await this.#readOutputFile(outputFilePath);
        return { ...result, output: content, truncated };
      }
      return result;
    } finally {
      if (tmpDir) {
        await rm(tmpDir, { recursive: true, force: true }).catch((e) => {
          this.#logger.warn(`Failed to clean up temp dir ${tmpDir}`, this.#asError(e));
        });
      }
    }
  }

  // Suspends the host TUI and hands the terminal to the command.
  //
  // `spawnSync` is deliberate (same invariant as `launchExternalEditor`):
  // blocking the event loop stops Ink from repainting over the child's screen.
  // Do not change to an async variant.
  //
  // No default timeout: killing an interactive session mid-use is worse than
  // letting the user finish; an explicit frontmatter timeout is honoured.
  #runInteractive(
    script: string,
    args: string[],
    options: PluginCommandRunOptions,
  ): PluginCommandRunResult {
    this.#logger.debug(`Running interactive plugin command in ${options.cwd}: ${script}`);
    const { isKittyProtocolSupported } = this.#runtimeContext.envInfo;

    const result = withSuspendedTty(
      () =>
        spawnSync('/bin/sh', ['-c', script, 'duo-plugin', ...args], {
          cwd: options.cwd,
          stdio: 'inherit',
          env: { ...process.env, ...options.env },
          ...(options.timeoutSeconds !== undefined
            ? { timeout: options.timeoutSeconds * 1000 }
            : {}),
        }),
      { isKittySupported: isKittyProtocolSupported },
    );

    const timedOut = (result.error as NodeJS.ErrnoException | undefined)?.code === 'ETIMEDOUT';
    if (result.error && !timedOut) {
      throw result.error;
    }

    return {
      output: '',
      stderr: '',
      exitCode: result.status,
      signal: result.signal ?? null,
      timedOut,
      truncated: false,
    };
  }

  async #runCaptured(
    script: string,
    args: string[],
    options: PluginCommandRunOptions,
  ): Promise<PluginCommandRunResult> {
    const timeoutMs = (options.timeoutSeconds ?? DEFAULT_TIMEOUT_SECONDS) * 1000;
    this.#logger.debug(`Running plugin command in ${options.cwd}: ${script}`);

    return new Promise<PluginCommandRunResult>((resolvePromise, rejectPromise) => {
      // `sh -c '<command>' duo-plugin <args...>`: args become $1..$n, so user
      // input never becomes shell syntax.
      //
      // `detached: true` makes the child a process-group leader so the timeout
      // can kill the whole group — compound commands (`&&`, `|`, loops) fork
      // children that would survive killing sh alone.
      const child = spawn('/bin/sh', ['-c', script, 'duo-plugin', ...args], {
        cwd: options.cwd,
        stdio: ['ignore', 'pipe', 'pipe'],
        env: { ...process.env, ...options.env },
        detached: true,
      });

      // Caps buffered output at MAX_OUTPUT_BYTES and decodes once in read(),
      // so split multi-byte UTF-8 characters never become replacement characters.
      const stdout = new CappedOutputSink(MAX_OUTPUT_BYTES);
      const stderr = new CappedOutputSink(MAX_OUTPUT_BYTES);
      let timedOut = false;
      let settled = false;
      let drainTimer: NodeJS.Timeout | undefined;

      const killTimer = setTimeout(() => {
        timedOut = true;
        this.#killProcessGroup(child, 'SIGTERM');
        setTimeout(() => this.#killProcessGroup(child, 'SIGKILL'), KILL_GRACE_MS).unref();
      }, timeoutMs);
      killTimer.unref();

      child.stdout.on('data', (chunk: Buffer) => stdout.push(chunk));
      child.stderr.on('data', (chunk: Buffer) => stderr.push(chunk));

      const settle = (exitCode: number | null, signal: NodeJS.Signals | null) => {
        if (settled) return;
        settled = true;
        clearTimeout(killTimer);
        clearTimeout(drainTimer);
        // Pipes held open by a background child would otherwise keep the
        // event loop alive until that child exits.
        child.stdout.destroy();
        child.stderr.destroy();
        resolvePromise({
          output: stdout.read(),
          stderr: stderr.read(),
          exitCode,
          signal,
          timedOut,
          // `truncated` describes `output` only; stderr is capped separately.
          truncated: stdout.truncated,
        });
      };

      child.on('error', (error) => {
        if (settled) return;
        settled = true;
        clearTimeout(killTimer);
        clearTimeout(drainTimer);
        rejectPromise(error);
      });

      // Happy path: all stdio drained and closed.
      child.on('close', (exitCode, signal) => settle(exitCode, signal));

      // Fallback: a background child holding the pipes prevents `close`, so
      // settle after a short drain grace. The background child is left
      // running — killing its group would break daemon-style plugins.
      child.on('exit', (exitCode, signal) => {
        if (settled) return;
        drainTimer = setTimeout(() => settle(exitCode, signal), STREAM_DRAIN_GRACE_MS);
        drainTimer.unref();
      });
    });
  }

  /**
   * Signals the child's whole process group so `sh -c` descendants die with
   * it. Falls back to the direct child when the group is already gone.
   */
  #killProcessGroup(
    child: { pid?: number; kill(signal: NodeJS.Signals): boolean },
    signal: NodeJS.Signals,
  ): void {
    if (child.pid !== undefined) {
      try {
        process.kill(-child.pid, signal);
        return;
      } catch {
        // ESRCH: group gone — fall through to the direct kill.
      }
    }
    child.kill(signal);
  }

  async #readOutputFile(path: string): Promise<{ content: string; truncated: boolean }> {
    let handle;
    try {
      // `open` (not `readFile`): the file may be arbitrarily large, so read
      // at most MAX_OUTPUT_BYTES instead of buffering it all.
      handle = await open(path, 'r');
    } catch (e) {
      if ((e as NodeJS.ErrnoException).code === 'ENOENT') {
        // The command chose not to write the file — empty output, not an error.
        return { content: '', truncated: false };
      }
      throw e;
    }
    try {
      const { size } = await handle.stat();
      const truncated = size > MAX_OUTPUT_BYTES;
      const buffer = Buffer.alloc(Math.min(size, MAX_OUTPUT_BYTES));
      const { bytesRead } = await handle.read(buffer, 0, buffer.length, 0);
      const decoder = new StringDecoder('utf8');
      const content = decoder.write(buffer.subarray(0, bytesRead));
      return { content: truncated ? content : content + decoder.end(), truncated };
    } finally {
      await handle.close();
    }
  }

  #asError(e: unknown): Error {
    return e instanceof Error ? e : new Error(String(e));
  }
}
